import React, { useState, useEffect } from 'react';
import Modal from 'react-responsive-modal';
import { SingleDatePicker } from 'react-dates';
import swal from 'sweetalert2';
import moment from 'moment';
import Select from 'react-select';
import 'react-dates/lib/css/_datepicker.css';
import TimePicker from '../../../../../TimePicker/TimePickerv2';
import LoadMask from '../../../Utils/LoadMask';
import CheckIcon from '../../../../icons/checkIcon';
import './ModalInterviewSchedule.css';
import { timezones } from '../../../../utility/constants';

export const ModalInterviewSchedule = ({
   show,
   toggleShowModal,
   handleInterviewSubmit,
   initialDateTime,
   initialTimezone,
}) => {
   const [submitFormLoading, setSubmitFormLoading] = useState(false);

   const [termDateFocused, setTermDateFocused] = useState(false);

   const [formValues, setFormValues] = useState({
      interview_date: null,
      interview_time: '',
      interview_timezone: 'EST',
   });

   useEffect(() => {
      if (show) {
         if (initialDateTime && initialTimezone) {
            const dateMoment = moment(initialDateTime);
            const hour = dateMoment.hour();
            const minutes = dateMoment.minute();
            const timeMoment = moment(`${hour}:${minutes}`, 'HH:mm');

            if (dateMoment.isValid()) {
               setFormValues({
                  interview_date: dateMoment,
                  interview_time: timeMoment,
                  interview_timezone: initialTimezone,
               });
            } else {
               setFormValues({
                  interview_date: null,
                  interview_time: '',
                  interview_timezone: 'EST',
               });
            }
         } else {
            setFormValues({
               interview_date: null,
               interview_time: '',
               interview_timezone: 'EST',
            });
         }
      }
   }, [show]);

   const handleChange = (name, value) => {
      if (name === 'interview_time') {
         const { hour, minutes } = value;

         if (hour != null && minutes != null) {
            const timeMoment = moment(`${hour}:${minutes}`, 'HH:mm');
            if (timeMoment.isValid()) {
               setFormValues((prev) => ({ ...prev, ['interview_time']: timeMoment }));
               // setTime(timeMoment);
            } else {
               console.error('Invalid time:', value);
            }
         }
      } else {
         setFormValues((prev) => ({
            ...prev,
            [name]: value,
         }));
      }
   };

   const handleSubmit = async () => {
      // Validaciones iniciales
      if (!formValues || !formValues.interview_date || !formValues.interview_time || !formValues.interview_timezone) {
         swal('Missing Fields', 'Please verify that required fields are filled', 'error');
         return;
      }

      // date cannot be in the past
      if (formValues.interview_date && formValues.interview_date.isBefore(moment(), 'day')) {
         swal('Invalid Date', 'Interview date cannot be in the past', 'error');
         return;
      }

      // if date is today, time must be greater than current time
      if (formValues.interview_date && formValues.interview_date.isSame(moment(), 'day')) {
         const now = moment();
         const interviewDateTime = moment(formValues.interview_time);
         if (interviewDateTime.isBefore(now)) {
            swal('Invalid Time', 'Interview time must be later than current time', 'error');
            return;
         }
      }

      try {
         // create a datetime value
         const interviewDateTime = moment(formValues.interview_date).set({
            hour: formValues.interview_time.hour(),
            minute: formValues.interview_time.minute(),
            second: 0,
            millisecond: 0,
         });

         handleInterviewSubmit(interviewDateTime, formValues.interview_timezone);
      } catch (error) {
         console.error('Error:', error);

         if (error.response) {
            swal('Error', error.response.data.error || 'An error occurred', 'error');
         } else if (error.request) {
            swal('Network Error', 'No response from server. Please try again later.', 'error');
         } else {
            swal('Error', 'An unexpected error occurred', 'error');
         }
      } finally {
         setSubmitFormLoading(false);
      }
   };

   const actionWord = initialDateTime ? 'Reschedule' : 'Schedule';

   return (
      <Modal
         open={show}
         onClose={() => toggleShowModal(false)}
         showCloseIcon={false}
         blockScroll={false}
         styles={{ modal: { minWidth: '35%', borderRadius: '8px', marginTop: 50, padding: 0 } }}
         closeOnOverlayClick={false}
      >
         <LoadMask loading={submitFormLoading} light>
            <div className="d-flex row m-0 justify-content-between align-items-center px-4 pt-3" style={{ gap: '4px' }}>
               <h4 style={{ fontWeight: 'bold' }}>{`${actionWord} Interview`}</h4>
            </div>

            <hr />

            <div className="container px-5">
               <div className="row mb-3">
                  <div className="col">
                     <h6 className="font-bold">Date</h6>
                     <div className="form-control p-1">
                        <SingleDatePicker
                           id="date_input"
                           date={formValues.interview_date}
                           onDateChange={(newDate) => handleChange('interview_date', newDate)}
                           focused={termDateFocused}
                           onFocusChange={({ focused }) => setTermDateFocused(focused)}
                           numberOfMonths={1}
                           isOutsideRange={(day) => day.isBefore(moment(), 'day')} // Disable past dates
                           placeholder="2025-01-01"
                        />
                     </div>
                  </div>
               </div>

               <div className="row mb-3">
                  <div className="col">
                     <h6 className="font-bold">Time</h6>
                     <TimePicker
                        id="timepicker1"
                        placeholder="12:00 PM"
                        value={formValues.interview_time}
                        onChange={(interview_time) => handleChange('interview_time', interview_time)}
                        style={{ height: '38px' }}
                     />
                  </div>
               </div>

               <div className="row mb-3">
                  <div className="col">
                     <h6 className="font-bold">Timezone</h6>
                     <Select
                        id="interview_timezone"
                        clearable={false}
                        className=""
                        backspaceRemovesValue={false}
                        searchable={true}
                        options={timezones}
                        placeholder="Timezone"
                        onChange={(e) => {
                           handleChange('interview_timezone', e.value);
                        }}
                        value={formValues.interview_timezone}
                        disabled={false}
                     />
                  </div>
               </div>

               <div className="row mb-3 mt-4">
                  <div className="col">
                     <div
                        className="d-flex flex-wrap justify-content-end align-items-center ml-auto"
                        style={{ gap: '13px' }}
                     >
                        <button
                           onClick={() => toggleShowModal(false)}
                           className="btn d-flex align-items-center rounded shadow-sm px-3 py-2 me-3"
                           style={{ backgroundColor: '#D50032', color: 'white' }}
                        >
                           <div style={{ display: 'flex', alignItems: 'center', height: '0.8rem' }}>X</div>
                           <span className="ml-1">Close</span>
                        </button>

                        <button
                           className="btn border border-dark rounded shadow-sm px-3 py-2 d-flex align-items-center"
                           onClick={handleSubmit}
                        >
                           <div style={{ display: 'flex', alignItems: 'center', height: '0.8rem' }}>
                              <CheckIcon className="h-100" />
                           </div>
                           <span className="ml-1" style={{ fontWeight: 'bold' }}>
                              {`Confirm ${actionWord}`}
                           </span>
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </LoadMask>
      </Modal>
   );
};
